import type { Base } from 'active-resource';
import { Collection } from 'active-resource';
import format from 'date-fns/format';
import { normalizeAbsoluteDate } from 'util/formatting/dates';
import type { LocationResource } from 'util/resources/Location';
import { jobUrl } from 'util/urlFactory';
import type { GConstructor } from './APILibrary';
import APILibrary, { ClassExtender, GenerateResource } from './APILibrary';
import { JobListingResource } from './JobListing';
import { UserProfileResource } from './UserProfile';

/**
 * Relationship classes
 */
export class JobApplicationAttributes {
  aasmState: string = null;
  id: string = null;
  createdAt: string = null;
  withdrawn: boolean = null;
  statusCategory: string = null;
  aiSuitabilitySummary: string = null;

  qualificationMatches: {
    maxRequirements?: number;
    metRequirementsCount?: number;
  } = null;

  screeningQuestions: { label: string; value: string }[] = null;
  userProfileId: string;
}

export class JobApplicationMeta {
  activeCount: number = null;
  withdrawnCount: number = null;
}

export class JobApplicationBelongsTo {
  jobListing: () => JobListingResource = () => new JobListingResource();
  userProfile: () => UserProfileResource = () => new UserProfileResource();
}

export class JobApplicationHasMany {
  locations: () => Collection<LocationResource> = () => new Collection<LocationResource>();
}

/**
 * Utility interfaces
 */
export interface JobApplicationMethods {
  formattedAppliedAt: () => string;
  jobListingTitle: () => string;
  jobListingUrl: () => string;
  firstAddress: () => string;
}

export interface JobApplicationInterface
  extends JobApplicationAttributes,
    JobApplicationBelongsTo,
    JobApplicationHasMany,
    JobApplicationType {}

type JobApplicationType = GConstructor<JobApplicationInterface>;

export interface JobApplicationResource
  extends Base<JobApplicationAttributes>,
    JobApplicationInterface,
    JobApplicationMethods {}

/**
 * Resource methods
 */
const BaseClass = () =>
  GenerateResource<JobApplicationAttributes>({
    queryName: 'job_applications',
    className: 'JobApplication',
    AttributesClass: JobApplicationAttributes,
    BelongsToClasses: [
      {
        Klass: JobApplicationBelongsTo,
      },
    ],
    HasManyClasses: [
      {
        Klass: JobApplicationHasMany,
      },
    ],
  });

export const JobApplicationResourceClass = <TBase extends JobApplicationType>(
  BaseConstructor: TBase,
): TBase =>
  class extends BaseConstructor implements JobApplicationMethods {
    formattedAppliedAt() {
      if (!this.createdAt) {
        return null;
      }

      return format(normalizeAbsoluteDate(new Date(this.createdAt)), 'MM/dd/yyyy');
    }

    jobListingTitle() {
      return this.jobListing().title;
    }

    jobListingUrl() {
      return jobUrl(this.jobListing());
    }

    firstAddress() {
      const locations = this.locations().toArray();
      if (locations?.length === 0) {
        return null;
      }
      return locations[0].address;
    }
  };

export class JobApplicationResource extends ClassExtender<JobApplicationInterface>(
  BaseClass,
  JobApplicationResourceClass,
) {}

/**
 * Resource instance
 */
export const JobApplication = APILibrary.createResource<JobApplicationResource, JobApplicationMeta>(
  JobApplicationResource,
);
